/**
 * AddVehicleModal
 * Modal för att registrera nytt fordon
 * Stöd för manuell inmatning och automatisk hämtning från Car.info
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import { BaseModal, ModalFooter } from '@/components/common/BaseModal';
import { resolveFuelTypeFromCarInfo } from '@/utils/resolveFuelTypeFromCarInfo';

interface AddVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVehicleCreated: () => void;
}

interface CarInfoData {
  success: boolean;
  registrationNumber?: string;
  vin?: string;
  brand?: string;
  model?: string;
  modelYear?: number;
  engineType?: string;
  engineName?: string;
  horsepower?: number;
  vehicleType?: string;
  isStolen?: boolean;
  hasDrivingBan?: string | null;
  errors?: string[];
}

type FuelType = 'Bensin' | 'Diesel' | 'El' | 'Hybrid' | 'Laddhybrid' | 'Gas' | 'Etanol' | '';
type VehicleStatus = 'ACTIVE' | 'MAINTENANCE' | 'INACTIVE' | 'RETIRED';

const AddVehicleModal: React.FC<AddVehicleModalProps> = ({ isOpen, onClose, onVehicleCreated }) => {
  const { t } = useTranslation();
  
  // Form state
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [vin, setVin] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState<number | ''>('');
  const [fuelType, setFuelType] = useState<FuelType>('');
  const [status, setStatus] = useState<VehicleStatus>('ACTIVE');
  const [organizationId, setOrganizationId] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [costCenter, setCostCenter] = useState('');
  const [initialOdometerKm, setInitialOdometerKm] = useState<number | ''>('');
  const [isPoolcar, setIsPoolcar] = useState(false);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingCarInfo, setIsFetchingCarInfo] = useState(false);
  const [carInfoFetched, setCarInfoFetched] = useState(false);
  const [carInfoError, setCarInfoError] = useState<string | null>(null);
  const [isStolen, setIsStolen] = useState(false);
  const [hasDrivingBan, setHasDrivingBan] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setRegistrationNumber('');
    setVin('');
    setMake('');
    setModel('');
    setYear('');
    setFuelType('');
    setStatus('ACTIVE');
    setOrganizationId('');
    setReferenceNumber('');
    setCostCenter('');
    setInitialOdometerKm('');
    setIsPoolcar(false);
    setCarInfoFetched(false);
    setCarInfoError(null);
    setIsStolen(false);
    setHasDrivingBan(false);
  };

  // Hämta data från Car.info
  const fetchFromCarInfo = async () => {
    if (!registrationNumber.trim()) {
      toast.error(t('fleet.addVehicle.enterRegNumber') || 'Ange registreringsnummer först');
      return;
    }

    setIsFetchingCarInfo(true);
    setCarInfoError(null);
    setCarInfoFetched(false);

    try {
      // Backend returnerar: { success: true, data: { registrationNumber, vin, brand, ... } }
      const response = await api.get<{
        success: boolean;
        fuelType?: FuelType | null;
        specifications?: unknown[];
        data: CarInfoData;
      }>(`/api/carinfo/lookup/${registrationNumber.trim()}`);
      
      // Kolla success på toppnivån och att data finns
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Fyll i formuläret med data från Car.info
        if (data.vin) setVin(data.vin);
        if (data.brand) setMake(data.brand);
        if (data.model) setModel(data.model);
        if (data.modelYear) setYear(data.modelYear);
        if (data.registrationNumber) setRegistrationNumber(data.registrationNumber);
        
        const mappedFuel =
          response.data.fuelType ??
          resolveFuelTypeFromCarInfo({
            engineType: data.engineType,
            specifications: response.data.specifications,
          });
        if (mappedFuel) setFuelType(mappedFuel as FuelType);
        
        // Varning om stulet fordon
        if (data.isStolen) {
          setIsStolen(true);
          toast.error(t('fleet.addVehicle.stolenWarning') || '⚠️ VARNING: Detta fordon är anmält stulet!');
        }
        if (data.hasDrivingBan === 'yes') {
          setHasDrivingBan(true);
          toast.error(t('fleet.addVehicle.drivingBanWarning'));
        }
        
        setCarInfoFetched(true);
        toast.success(t('fleet.addVehicle.carInfoSuccess') || 'Fordonsdata hämtad från Car.info');
      } else {
        setCarInfoError(response.data.data?.errors?.join(', ') || t('fleet.addVehicle.carInfoNotFound') || 'Fordon hittades inte');
        toast.error(t('fleet.addVehicle.carInfoNotFound') || 'Fordon hittades inte i Car.info');
      }
    } catch (error: any) {
      console.error('Car.info error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Kunde inte hämta data';
      setCarInfoError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsFetchingCarInfo(false);
    }
  };

  // Spara fordon
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Validering
    if (!registrationNumber.trim()) {
      toast.error(t('fleet.addVehicle.regRequired') || 'Registreringsnummer krävs');
      return;
    }
    if (!organizationId) {
      toast.error(t('fleet.addVehicle.orgRequired') || 'Välj en organisation');
      return;
    }

    setIsLoading(true);

    try {
      const vehicleData = {
        registration_number: registrationNumber.trim().toUpperCase().replace(/\s/g, ''),
        vin: vin.trim() || undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
        year: year || undefined,
        fuel_type: fuelType || undefined,
        status,
        organization_id: organizationId,
        reference_number: referenceNumber.trim() || undefined,
        cost_center: costCenter.trim() || undefined,
        // Ingående mätarställning: användaren anger km, vi lagrar i meter
        initial_odometer: initialOdometerKm ? initialOdometerKm * 1000 : undefined,
        is_poolcar: isPoolcar,
      };

      const response = await api.post('/api/vehicles', vehicleData);
      
      if (response.data.success) {
        toast.success(t('fleet.addVehicle.success') || 'Fordon registrerat!');
        onVehicleCreated();
        onClose();
      } else {
        toast.error(response.data.error || t('fleet.addVehicle.error') || 'Kunde inte skapa fordon');
      }
    } catch (error: any) {
      console.error('Create vehicle error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Kunde inte skapa fordon';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('fleet.addVehicle.title') || 'Registrera nytt fordon'}
      size="2xl"
      loading={isLoading}
      footer={
        <ModalFooter
          onCancel={onClose}
          onSubmit={handleSubmit}
          cancelText={t('common.cancel') || 'Avbryt'}
          submitText={t('fleet.addVehicle.save') || 'Spara fordon'}
          loading={isLoading}
          disabled={!registrationNumber.trim() || !organizationId}
        />
      }
    >
      <form id="add-vehicle-form" onSubmit={handleSubmit} className="space-y-4">
            {/* Registreringsnummer + Car.info-knapp */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('fleet.registrationNumber') || 'Registreringsnummer'} *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={registrationNumber}
                  onChange={(e) => {
                    setRegistrationNumber(e.target.value.toUpperCase());
                    setCarInfoFetched(false);
                  }}
                  placeholder="ABC123"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent uppercase"
                  maxLength={10}
                />
                <button
                  type="button"
                  onClick={fetchFromCarInfo}
                  disabled={isFetchingCarInfo || !registrationNumber.trim()}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    carInfoFetched 
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed'
                  }`}
                >
                  {isFetchingCarInfo ? (
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  ) : carInfoFetched ? (
                    <CheckIcon className="w-5 h-5" />
                  ) : (
                    <MagnifyingGlassIcon className="w-5 h-5" />
                  )}
                  <span className="hidden sm:inline">
                    {isFetchingCarInfo 
                      ? (t('common.loading') || 'Laddar...')
                      : carInfoFetched 
                        ? (t('fleet.addVehicle.fetched') || 'Hämtad')
                        : (t('fleet.addVehicle.fetchCarInfo') || 'Hämta från Car.info')}
                  </span>
                </button>
              </div>
              {carInfoError && (
                <p className="mt-1 text-sm text-red-600">{carInfoError}</p>
              )}
              {carInfoFetched && (
                <p className="mt-1 text-sm text-green-600">
                  {t('fleet.addVehicle.carInfoLoaded') || 'Fordonsdata laddad från Car.info'}
                </p>
              )}
            </div>

            {/* Varning för stulet fordon */}
            {isStolen && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 rounded-lg flex items-center gap-2">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                <span className="text-red-800 font-medium">
                  {t('fleet.addVehicle.stolenWarning') || 'VARNING: Detta fordon är anmält stulet!'}
                </span>
              </div>
            )}
            {hasDrivingBan && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 rounded-lg flex items-center gap-2">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                <span className="text-red-800 font-medium">
                  {t('fleet.addVehicle.drivingBanWarning')}
                </span>
              </div>
            )}

            {/* Organisation */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('fleet.organization') || 'Organisation'} *
              </label>
              <OrgTreeSelect
                value={organizationId}
                onChange={setOrganizationId}
                placeholder={t('fleet.addVehicle.selectOrg') || 'Välj organisation'}
                showEmptyOption
                emptyLabel={t('fleet.addVehicle.selectOrg') || 'Välj organisation'}
              />
            </div>

            {/* Grid för övriga fält */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {/* VIN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  VIN
                </label>
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => setVin(e.target.value.toUpperCase())}
                  placeholder="17-siffrig VIN-kod"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent uppercase"
                  maxLength={17}
                />
              </div>

              {/* Märke */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fleet.make') || 'Märke'}
                </label>
                <input
                  type="text"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  placeholder="Volvo, Toyota..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Modell */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fleet.model') || 'Modell'}
                </label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="XC60, Corolla..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Årsmodell */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fleet.year') || 'Årsmodell'}
                </label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value ? parseInt(e.target.value) : '')}
                  placeholder="2024"
                  min={1900}
                  max={new Date().getFullYear() + 1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Bränsletyp */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fleet.fuelType') || 'Bränsletyp'}
                </label>
                <select
                  value={fuelType}
                  onChange={(e) => setFuelType(e.target.value as FuelType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                >
                  <option value="">{t('common.select') || 'Välj...'}</option>
                  <option value="Bensin">{t('fleet.fuelTypes.petrol') || 'Bensin'}</option>
                  <option value="Diesel">{t('fleet.fuelTypes.diesel') || 'Diesel'}</option>
                  <option value="El">{t('fleet.fuelTypes.electric') || 'El'}</option>
                  <option value="Hybrid">{t('fleet.fuelTypes.hybrid') || 'Hybrid'}</option>
                  <option value="Laddhybrid">{t('fleet.fuelTypes.pluginHybrid') || 'Laddhybrid'}</option>
                  <option value="Gas">{t('fleet.fuelTypes.gas') || 'Gas'}</option>
                  <option value="Etanol">{t('fleet.fuelTypes.ethanol') || 'Etanol'}</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fleet.status') || 'Status'}
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as VehicleStatus)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                >
                  <option value="ACTIVE">{t('fleet.statusActive') || 'Aktiv'}</option>
                  <option value="MAINTENANCE">{t('fleet.statusMaintenance') || 'Under underhåll'}</option>
                  <option value="INACTIVE">{t('fleet.statusInactive') || 'Inaktiv'}</option>
                  <option value="RETIRED">{t('fleet.statusRetired') || 'Avvecklad'}</option>
                </select>
              </div>

              {/* Referensnummer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fleet.referenceNumber') || 'Referensnummer'}
                </label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="Internt ref.nr"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Kostnadsställe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fleet.costCenter') || 'Kostnadsställe'}
                </label>
                <input
                  type="text"
                  value={costCenter}
                  onChange={(e) => setCostCenter(e.target.value)}
                  placeholder={t('fleet.costCenter')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Ingående mätarställning */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fleet.initialOdometer') || 'Ingående mätarställning (km)'}
                </label>
                <input
                  type="number"
                  value={initialOdometerKm}
                  onChange={(e) => setInitialOdometerKm(e.target.value ? parseInt(e.target.value) : '')}
                  placeholder="0"
                  min={0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p className="mt-1 text-xs text-textTertiary">
                  {t('fleet.initialOdometerHint') || 'Mätarställning vid registrering'}
                </p>
              </div>
            </div>

            {/* Poolbil checkbox */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPoolcar}
                  onChange={(e) => setIsPoolcar(e.target.checked)}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="text-sm text-gray-700 dark:text-textDefault">
                  {t('fleet.isPoolcar') || 'Poolbil'}
                </span>
              </label>
            </div>
      </form>
    </BaseModal>
  );
};

export default AddVehicleModal;
